import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  AuthProvider,
  CryptoProvider,
  useAuth,
  AccessDeniedScreen,
  createAdminQueryClient,
  useAutoSelectValidRole,
  type Membership,
} from "@workspace/admin-ui";
import { theme } from "@/lib/theme";
import { ToastProvider } from "@/lib/toast";

import LoginPage from "@/pages/Login";
import DashboardPage from "@/pages/Dashboard";
import PharmacistsPage from "@/pages/Pharmacists";
import PharmacistDetailPage from "@/pages/PharmacistDetail";
import ConsultationsPage from "@/pages/Consultations";
import ConsultationDetailPage from "@/pages/ConsultationDetail";
import BookingsPage from "@/pages/Bookings";
import RoomsPage from "@/pages/Rooms";
import { AppShell } from "@/components/AppShell";

const queryClient = createAdminQueryClient();

const isPharmacyAdmin = (m: Membership) =>
  m.status === "active" &&
  m.role === "pharmacy_admin" &&
  !!m.chainId &&
  !!m.pharmacyId;

function Gate() {
  const { user, membership, loading } = useAuth();
  const auto = useAutoSelectValidRole(isPharmacyAdmin);

  if (loading || (user && auto.switching)) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!user) return <LoginPage />;

  if (
    !membership || membership.status !== "active" || membership.role !== "pharmacy_admin" || !membership.chainId || !membership.pharmacyId
  ) {
    return <AccessDeniedScreen requiredRole="pharmacy_admin" />;
  }

  return (
    <AppShell>
      <Switch>
        <Route path="/" component={DashboardPage} />
        <Route path="/pharmacists" component={PharmacistsPage} />
        <Route path="/pharmacists/:id" component={PharmacistDetailPage} />
        <Route path="/consultations" component={ConsultationsPage} />
        <Route path="/consultations/:id" component={ConsultationDetailPage} />
        <Route path="/bookings" component={BookingsPage} />
        <Route path="/rooms" component={RoomsPage} />
        <Route>
          <Box sx={{ p: 4 }}>
            <Typography variant="h6">Not found</Typography>
          </Box>
        </Route>
      </Switch>
    </AppShell>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <AuthProvider panel="pharmacy-admin">
          <CryptoProvider>
            <ToastProvider>
              <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                <Gate />
              </WouterRouter>
            </ToastProvider>
          </CryptoProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
